import React, { useMemo, useRef, useState } from "react";

/* ----- data ----- */
const rows = [
  { channel: "WhatsApp Ads", time: "≈ 90 sec", conv: "High (x2–x3)", trust: "Very High" },
  { channel: "Google Ads", time: "1–2 days", conv: "Medium", trust: "Medium" },
  { channel: "Email", time: "12–24 hrs", conv: "Low (2–5%)", trust: "Low" },
  { channel: "SMS", time: "Few hours", conv: "Medium", trust: "Medium" },
];

const colors = ["#ff667d", "#ff8a80", "#ffb07b", "#d7eaa0", "#8adbb8", "#a9b4e9"];

/* messages shown in sequence on button clicks */
const demoMessages = [
  "Fastest path: Ad → WhatsApp → Reply in under 90 sec ⏱️",
  "Trust boost: users chat on WhatsApp, not cold landing pages ✅",
  "Higher intent: conversation captures context + objections 💬",
  "Lower CPA: automation qualifies & routes leads automatically 🤖",
  "Conversion logged: catalog, pay link, or agent handoff 📈",
];

export default function Comparison() {
  const [step, setStep] = useState(0);
  const msg = demoMessages[step % demoMessages.length];

  // ripple click effect (pure CSS vars)
  const btnRef = useRef(null);
  const onClickShow = (e) => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--rx", `${e.clientX - r.left}px`);
    el.style.setProperty("--ry", `${e.clientY - r.top}px`);
    // advance message
    setStep((s) => s + 1);
  };

  // precompute WhatsApp row highlight
  const waRowIndex = useMemo(() => rows.findIndex((r) => r.channel === "WhatsApp Ads"), []);

  return (
    <div className="cmp-wrap">
      {/* Header */}
      <header className="cmp-head">
        <div className="cmp-head-row">
          <div>
            <h2 className="cmp-title">Why brands prefer WhatsApp Ads</h2>
            <p className="cmp-tag">Benchmarks compared with other acquisition channels.</p>
          </div>

          {/* Show button */}
          <button
            ref={btnRef}
            className="vx-btn"
            onClick={onClickShow}
            aria-label="Show next highlight"
          >
            <span className="vx-btn-label">Show me</span>
            <span className="vx-ripple" aria-hidden="true" />
          </button>
        </div>

        {/* Rotating message (changes each click) */}
        <div className="cmp-msg" role="status" aria-live="polite" key={step}>
          <span className="dot" aria-hidden="true">•</span>
          <span className="txt">{msg}</span>
        </div>
      </header>

      {/* Card with left “port” animation */}
      <div className="cmp-card">
        {/* Left rail */}
        <div className="cmp-port" aria-hidden="true">
          {colors.map((c, i) => (
            <span className="p-dot" key={i} style={{ background: c, animationDelay: `${i * 0.18}s` }} />
          ))}
        </div>

        <table className="cmp-table">
          <thead>
            <tr>
              <th>Channel</th>
              <th>Avg. Response</th>
              <th>Conversion Rate</th>
              <th>Trust Level</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i === waRowIndex ? "is-wa" : ""}>
                <td className="strong" data-label="Channel">{r.channel}</td>
                <td data-label="Avg. Response">{r.time}</td>
                <td data-label="Conversion Rate">{r.conv}</td>
                <td data-label="Trust Level">{r.trust}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Scoped CSS */}
      <style>{`
        /* THEME: tune brand + ink to your Vertex palette */
        :root {
          --brand: #1DB954;      /* WhatsApp green */
          --brand-700: #169c43;  /* darker green */
          --ink: #0f172a;        /* headings */
          --muted: #64748b;      /* body-muted */
          --border: #e6edf5;
          --focus: #60a5fa;
          --shadow: 0 10px 28px rgba(2, 6, 23, 0.06);
        }
        :root, html, body { background: #ffffff; }
        .cmp-wrap, .cmp-wrap * { box-sizing: border-box; }

        .cmp-wrap {
          max-width: 1450px;
          margin: 0 auto;
          padding: 56px 60px 72px;
          background: #ffffff;
          color: var(--ink);
          font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
        }

        /* Title Row */
        .cmp-head { margin-bottom: 22px; }
        .cmp-head-row {
          display: flex;
          align-items: end;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .cmp-title { margin: 0; font-size: 2.5rem; font-weight: 900; line-height: 1.12; letter-spacing: 0.2px; }
        .cmp-tag { margin: 8px 0 0; max-width: 720px; font-size: 1rem; color: var(--muted); }

        /* ===== Brand-styled button (text matches heading color family) ===== */
        .vx-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border-radius: 999px;
          background: #ffffff;
          color: var(--brand); /* text in green */
          border: 1.5px solid var(--brand); /* outline in green */
          font-weight: 800;
          letter-spacing: 0.2px;
          transition: transform 0.12s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;
          outline: none;
          overflow: hidden; /* for ripple */
        }
        .vx-btn:hover {
          background: var(--brand);
          color: #fff;
          box-shadow: 0 10px 24px rgba(29, 185, 84, 0.25);
          transform: translateY(-1px);
        }
        .vx-btn:active {
          transform: translateY(0);
          box-shadow: 0 6px 16px rgba(29, 185, 84, 0.2);
        }
        .vx-btn:focus-visible {
          box-shadow: 0 0 0 3px var(--focus);
        }
        .vx-btn .vx-btn-label {
          position: relative;
          z-index: 1; /* above ripple */
        }
        /* ripple uses CSS vars --rx/--ry set on click */
        .vx-ripple {
          position: absolute;
          inset: 0;
          background: radial-gradient(200px circle at var(--rx, 50%) var(--ry, 50%), rgba(255, 255, 255, 0.5), transparent 60%);
          opacity: 0;
          transform: scale(0.9);
          transition: opacity 0.5s ease, transform 0.5s ease;
          pointer-events: none;
        }
        .vx-btn:active .vx-ripple { opacity: 1; transform: scale(1); }

        /* ===== Rotating message (changes each click) ===== */
        .cmp-msg {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 14px;
          background: linear-gradient(0deg, #f9fbff, #ffffff);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 10px 12px;
          box-shadow: 0 6px 16px rgba(2, 6, 23, 0.06);
          animation: fadeSlide 0.45s ease both;
        }
        .cmp-msg .dot { color: var(--brand); font-weight: 900; }
        .cmp-msg .txt { color: var(--ink); }
        @keyframes fadeSlide { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

        /* ===== Card + Table with left port ===== */
        .cmp-card {
          position: relative;
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 18px;
          box-shadow: var(--shadow);
          padding: 10px 12px 10px 20px;
          overflow: hidden;
          margin-top: 16px;
        }

        .cmp-port {
          position: absolute;
          top: 10px;
          bottom: 10px;
          left: 10px;
          width: 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          pointer-events: none;
        }
        .cmp-port .p-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          filter: drop-shadow(0 4px 8px rgba(2, 6, 23, 0.15));
          animation: flowDown 1.6s ease-in-out infinite;
          opacity: 0.95;
        }
        @keyframes flowDown {
          0% { transform: translateY(-8px); opacity: 0.7; }
          50% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(8px); opacity: 0.7; }
        }

        .cmp-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.98rem;
          margin-left: 6px;
        }
        .cmp-table thead th {
          text-align: left;
          font-weight: 700;
          color: var(--muted);
          padding: 16px 20px;
          border-bottom: 1px solid #e9eff6;
          background: linear-gradient(180deg, #ffffff, #f9fbfe);
        }
        .cmp-table tbody td {
          padding: 18px 20px;
          border-top: 1px solid #eef2f7;
          transition: background 0.2s ease;
        }
        .cmp-table tbody tr:first-child td { border-top: none; }
        .cmp-table tbody tr { transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease; }
        .cmp-table tbody tr:hover { background: #f8fbff; transform: translateY(-1px); box-shadow: 0 8px 20px rgba(2, 6, 23, 0.05); }
        .cmp-table tbody tr.is-wa { background: linear-gradient(0deg, #f6fff9, #ffffff); }
        .strong { font-weight: 900; }

        /* Mobile: rows become mini-cards */
        @media (max-width: 760px) {
          .cmp-card { padding: 0 10px 10px 10px; }
          .cmp-port { left: 6px; }
          .cmp-table, .cmp-table thead, .cmp-table tbody, .cmp-table th, .cmp-table td, .cmp-table tr { display: block; }
          .cmp-table thead { display: none; }
          .cmp-table tbody tr {
            border-top: 1px solid #e9eff6;
            padding: 14px 12px 14px 18px;
            border-radius: 14px;
            margin: 10px 6px 0 18px;
            background: #fff;
          }
          .cmp-table tbody tr:first-child { margin-top: 0; border-top: none; }
          .cmp-table tbody td { border: none; padding: 6px 0; }
          .cmp-table tbody td::before {
            content: attr(data-label);
            display: block;
            font-size: 0.85rem;
            color: var(--muted);
            margin-bottom: 2px;
          }
        }

        /* motion safe */
        @media (prefers-reduced-motion: reduce) {
          .vx-btn, .vx-ripple, .cmp-msg, .cmp-table tbody tr, .cmp-port .p-dot { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
}